// src/pages/emailLogin/hooks/useEmailLogin.ts
import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  getFromStorage,
  saveToStorage,
} from "../../common/utils/functions/localStorage";
import { getApi } from "../../api/api";
import { EmailStage } from "../../common/types/enums";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import useInterval from "@use-it/interval";
import { delayAction } from "../../common/utils/functions/delayAction";
import { ROUTES } from "../../common/utils/constants";
import {
  openCartModal,
  openComplexOrderCartModal,
} from "../../store/slices/commonModalSlice";

export const useEmailLogin = (
  attach?: boolean,
  initialEmail?: string,
  onAttachComplete?: () => void,
) => {
  const [stage, setStage] = useState<EmailStage>(EmailStage.emailInput);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [legalityChecked, setLegalityChecked] = useState(false);
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [registerMessage, setRegisterMessage] = useState("");

  const api = getApi();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentCluster = useSelector(selectCurrentCluster);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.(ru|рф)$/i;
    return emailRegex.test(email) && email.length >= 5;
  };

  useInterval(() => {
    if (timer > 0) {
      setTimer((prev) => prev - 1);
    }
  }, 1000);

  const handleAfterAction = useCallback(() => {
    const afterAction = getFromStorage("afterAction");
    if (!afterAction) return;

    setTimeout(() => {
      switch (afterAction) {
        case "cart":
          dispatch(openCartModal());
          break;
        case "complexCart":
          dispatch(openComplexOrderCartModal());
          break;
      }
    }, 500);
  }, [dispatch]);

  const requestCode = useCallback(
    async (email: string) => {
      if (!isValidEmail(email)) {
        setMessage("Введите корректный email (например: name@domain.ru)");
        setStage(EmailStage.error);
        return;
      }

      try {
        setLoading(true);
        setMessage("Отправка кода...");

        if (attach) {
          await api.requestAttachEmailCode({
            email,
            clusterId: currentCluster?.id,
          });
        } else {
          await api.requestEmailCode({
            email,
            clusterId: currentCluster?.id,
          });
        }

        setEmail(email);
        setStage(EmailStage.codeInput);
        setTimer(60);
        setMessage("");
        setRegisterMessage("");
        setCode("");
      } catch (error: any) {
        const errorText = (
          error?.response?.data?.message ||
          error?.message ||
          ""
        ).toLowerCase();

        if (errorText.includes("timeout") && errorText.includes("exceeded")) {
          setMessage("Время ожидания истекло, попробуйте позже");
        } else if (error.response?.status === 401) {
          setMessage("Ошибка авторизации, попробуйте позже");
        } else if (error.response?.status === 429) {
          setMessage("Слишком много попыток, повторите через минуту");
        } else {
          setMessage(error.message || "Ошибка отправки кода");
        }
        setStage(EmailStage.error);
      } finally {
        setLoading(false);
      }
    },
    [currentCluster, api, attach],
  );

  const verifyCode = useCallback(
    async (code: string) => {
      try {
        setLoading(true);
        setMessage("Проверка кода...");
        await delayAction(700);

        const result = await api.verifyEmailCode({
          email,
          code,
        });

        if (result.type === "attach_success") {
          if (result.message) setMessage(result.message);
          await delayAction(2000);
          if (attach) {
            onAttachComplete?.();
          }
          navigate(ROUTES.MAIN, { replace: true });
          return;
        }

        if (result.registerAction) {
          setIsLogin(false);
          setRegisterMessage(
            "Мы не нашли ваш аккаунт в нашей системе. После нажатия кнопки будет выполнена регистрация. Пожалуйста, подтвердите согласие с документами ниже",
          );
          setMessage("");
          return;
        }

        if (result.accessToken) {
          if (attach) {
            setMessage("Email успешно привязан");
          } else {
            setMessage("Авторизация успешна");
          }

          saveToStorage("accessToken", result.accessToken);
          if (result.refreshToken) {
            saveToStorage("refreshToken", result.refreshToken);
          }

          await delayAction(1000);
          setStage(EmailStage.done);
          navigate(ROUTES.MAIN, { replace: true });
          handleAfterAction();
        }
      } catch (error: any) {
        const errorText = (
          error?.response?.data?.message ||
          error?.message ||
          ""
        ).toLowerCase();

        if (errorText.includes("timeout") && errorText.includes("exceeded")) {
          setMessage("Время ожидания истекло, попробуйте позже");
        } else if (
          error.code === "ERR_NETWORK" ||
          error.code === "ECONNABORTED" ||
          (!error.response && !error.status)
        ) {
          setMessage("Нет подключения к интернету. Проверьте сеть и повторите");
        } else if (error.response?.status === 401) {
          setMessage("Неверный или просроченный код");
        } else if (error.response?.status === 429) {
          setMessage("Слишком много попыток, повторите позже");
        } else {
          setMessage(error.message || "Ошибка проверки кода");
        }
      } finally {
        setLoading(false);
      }
    },
    [
      email,
      api,
      navigate,
      dispatch,
      attach,
      onAttachComplete,
      handleAfterAction,
    ],
  );

  const register = useCallback(async () => {
    if (!legalityChecked || loading) return;

    try {
      setLoading(true);
      setMessage("Регистрация...");
      await delayAction(700);

      const result = await api.register({
        email,
        code,
      });

      if (result.accessToken) {
        setMessage("Вы зарегистрированы");

        saveToStorage("accessToken", result.accessToken);
        if (result.refreshToken) {
          saveToStorage("refreshToken", result.refreshToken);
        }

        await delayAction(1000);
        setStage(EmailStage.done);
        navigate(ROUTES.MAIN, { replace: true });
        handleAfterAction();
      }
    } catch (error: any) {
      const errorText = (
        error?.response?.data?.message ||
        error?.message ||
        ""
      ).toLowerCase();

      if (errorText.includes("timeout") && errorText.includes("exceeded")) {
        setMessage("Время ожидания истекло, попробуйте позже");
      } else if (error.response?.status === 401) {
        setMessage("Неверный или просроченный код");
      } else {
        setMessage(error.message || "Ошибка регистрации");
      }
    } finally {
      setLoading(false);
    }
  }, [
    email,
    code,
    api,
    navigate,
    dispatch,
    legalityChecked,
    loading,
    handleAfterAction,
  ]);

  const resendCode = useCallback(() => {
    if (email && timer === 0) {
      requestCode(email);
    }
  }, [email, timer, requestCode]);

  const toggleLegality = useCallback(() => {
    setLegalityChecked((prev) => !prev);
  }, []);
  

  return {
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    email,
    code,
    timer,
    setCode,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    setStage,
    isValidEmail,
  };
};
